/**
 * `CapabilityRegistry` — single per-session registry of {@link Capability}s.
 *
 * The registry is the source of truth for everything the LLM can invoke
 * during a session. Both the platform (via `ConfigureCommand.capabilities`
 * and {@link CapabilityRegisterCommand}) and the runtime (skill loaders,
 * flow adapters, MCP/connector/mount surfaces) call {@link register}; the
 * bridge driver pulls the LLM tool list via {@link composeLLMTools}.
 *
 * Trust enforcement: each call to {@link register} carries the registering
 * `source` (`'client'` or `'agent'`); the registry rejects mismatches such
 * as the agent claiming a `'client'` origin or vice-versa, logging a warning
 * and dropping the capability.
 *
 * Per-invocation logging: {@link invoke} constructs a
 * `createLogger({kind: 'capability', subkind: origin.kind, instance: name})`
 * and threads it through {@link HandlerContext} so handler output lands under
 * the `capability:<origin>:<name>` slice — `capability:app:<appId>.<name>` for
 * an embedded app, so invocations are attributable per app.
 *
 * Hibernation: {@link serialize} / {@link hydrate} round-trip the wire-format
 * capabilities. Agent-side capabilities (skill / flow / mcp / connector /
 * mount) are expected to re-register from their original sources on wake;
 * client-side capabilities are pushed back via
 * {@link ConfigureCommandV2 ConfigureCommand} from the platform.
 *
 * @category Runtime
 * @since 2.0.0
 */
import { createHash } from "node:crypto";
import { createLogger } from "@skaile/workspaces/core/logging";
import type {
  Capability,
  CapabilityAudience,
  CapabilityOrigin,
  Logger,
} from "@skaile/workspaces/types";
import type { DefinedCapability, HandlerContext } from "./define-capability.js";

/**
 * Default audience scope applied when a {@link Capability} omits `audience`.
 * Matches the v2.x behaviour where every registered capability appears in
 * both the LLM tool list and the command palette.
 */
const DEFAULT_AUDIENCE: readonly CapabilityAudience[] = ["llm", "user"];

function audienceOf(cap: Capability): readonly CapabilityAudience[] {
  return cap.audience ?? DEFAULT_AUDIENCE;
}

function hasAudience(cap: Capability, target: CapabilityAudience): boolean {
  return audienceOf(cap).includes(target);
}

/**
 * Function shape for the platform-bound capability invoker.
 *
 * The runner uses this to dispatch `host.*` capability invocations (e.g.
 * `host.refresh_credential`, `host.audit`, `host.notify_user`) over the
 * transport and await the matching `CapabilityResultCommand`. Implementations
 * own the wire serialization and the per-call pending map.
 *
 * The result value is the capability's domain payload (already
 * deserialised; for `host.refresh_credential` this is a `CredentialMint`).
 * Capability-dispatch infrastructure failures (timeout, schema validation,
 * transport disconnect) are thrown as `Error` so callers can distinguish
 * them from domain failures encoded in the payload.
 *
 * @category Runtime
 * @since 3.0.0
 * @docLink packages/runner/capabilities#remote-invoker
 */
export type RemoteCapabilityInvoker = (
  name: string,
  input: unknown,
  timeoutMs?: number,
) => Promise<unknown>;

/**
 * Minimal LLM tool descriptor consumed by bridge drivers. Drivers translate
 * this into the provider-specific tool spec (Claude tool definition, OpenAI
 * function spec, etc.) at registration time.
 *
 * Only the fields needed for tool registration are surfaced here; runtime
 * dispatch flows through {@link CapabilityRegistry.invoke}.
 *
 * @category Runtime
 * @since 2.0.0
 * @docLink packages/runner/capabilities#registry-surface
 */
export type LLMTool = {
  /** Capability name; used as the LLM-visible tool name. */
  name: string;
  /** Human/LLM-readable description. */
  description: string;
  /** JSON Schema for the tool's input parameters. */
  parameters: Record<string, unknown>;
};

/**
 * Filter shape accepted by {@link CapabilityRegistry.list}. All fields are
 * optional and match exactly when present.
 *
 * @category Runtime
 * @since 2.0.0
 * @docLink packages/runner/capabilities#registry-surface
 */
export type CapabilityListFilter = {
  side?: "client" | "agent" | "app";
  origin?: CapabilityOrigin["kind"];
  scope?: "session" | "turn";
};

/**
 * Side-tag declaring who is registering a capability. The registry uses
 * this to enforce origin trust — a client cannot register an `agent`-side
 * origin, the agent cannot register `client` / `app` origins, and an
 * embedded app may register only its own `app` origin.
 *
 * The `app` source carries the registering connection's `appId` so the
 * registry can assert that an `app`-origin capability is declared for the
 * connection it actually arrived on (a compromised source cannot register
 * `app.delete_everything` under someone else's `appId`). Bare-string
 * `client` / `agent` sources are unchanged.
 *
 * @category Runtime
 * @since 2.0.0
 * @docLink packages/runner/capabilities#registry-surface
 */
export type RegisterSource = "client" | "agent" | { kind: "app"; appId: string };

/**
 * Reserved namespace for embedded-app capabilities. The platform gateway rejects
 * an app registration outside it, which makes the prefix a reliable marker even
 * for a capability whose origin has been relabelled.
 */
const APP_CAPABILITY_PREFIX = "app.";

/**
 * The `instance` segment of a capability's log slice. An app capability carries
 * its `appId`, yielding `capability:app:<appId>.<name>` — so an operator can
 * attribute an invocation to the app that answered it, not just to its name.
 *
 * The `appId.name` join is unambiguous because an `appId` cannot contain a dot:
 * the platform validates it as `/^[a-z0-9-]+$/` before an app connection is ever
 * bound. Nothing here re-checks it; a dotted `appId` would only blur the slice.
 */
export function capabilityLogInstance(cap: Pick<Capability, "name" | "origin">): string {
  return cap.origin.kind === "app" ? `${cap.origin.appId}.${cap.name}` : cap.name;
}

const ORIGIN_PROMPT_ORDER: CapabilityOrigin["kind"][] = [
  "framework",
  "client",
  "agent",
  "flow",
  "skill",
  "mcp",
  "connector",
  "mount",
  "app",
];

/** Stable string label for a {@link RegisterSource} in structured logs. */
function describeSource(source: RegisterSource): string {
  return typeof source === "string" ? source : `app:${source.appId}`;
}

/**
 * Per-session registry of capabilities. Construct one per
 * `startAgentServer` invocation; the runner threads it through serve mode's
 * command handlers and the bridge.
 *
 * @category Runtime
 * @since 2.0.0
 * @docLink packages/runner/capabilities#registry-surface
 */
export class CapabilityRegistry {
  private readonly byName = new Map<string, DefinedCapability>();
  private readonly logger: Logger;
  private remoteInvoker?: RemoteCapabilityInvoker;
  private readonly toolListeners = new Set<() => void>();

  /** Observe inventory changes without caching a second tool registry. */
  onToolsChanged(listener: () => void): () => void {
    this.toolListeners.add(listener);
    return () => {
      this.toolListeners.delete(listener);
    };
  }

  private toolsChanged(): void {
    for (const listener of this.toolListeners) {
      try {
        listener();
      } catch {
        /* Observers cannot roll back a registry mutation. */
      }
    }
  }

  constructor(logger?: Logger) {
    this.logger = logger ?? createLogger({ kind: "runner", subkind: "capability-registry" });
  }

  /**
   * Install the platform-bound invoker used by {@link invokeRemote}.
   *
   * The runner wires this up after the transport is connected and the
   * per-session pending-call map is in place. Idempotent — repeated calls
   * replace the prior invoker.
   *
   * @category Runtime
   * @since 3.0.0
   */
  setRemoteInvoker(invoker: RemoteCapabilityInvoker | undefined): void {
    this.remoteInvoker = invoker;
  }

  /**
   * Invoke a `host.*` (or other peer-side) capability on the platform.
   *
   * Composes a `capability_invoked` event under the hood and resolves with
   * the matching `capability_result` payload. Infrastructure failures
   * (timeout, schema mismatch, transport disconnect) reject with `Error`;
   * domain failures (e.g. `{ ok: false, code }` for credential mints) ride
   * in the resolved payload.
   *
   * @category Runtime
   * @since 3.0.0
   */
  async invokeRemote(name: string, input: unknown): Promise<unknown> {
    if (!this.remoteInvoker) {
      throw new Error(
        `Capability '${name}' invoked remotely but no remote invoker is wired. ` +
          `Call CapabilityRegistry.setRemoteInvoker(...) before dispatch.`,
      );
    }
    // Honour a per-capability callTimeoutMs for outbound host.* calls,
    // mirroring the inbound resolveCapabilityCallTimeoutMs path.
    return this.remoteInvoker(name, input, this.byName.get(name)?.callTimeoutMs);
  }

  /**
   * Register a capability. Validates that the registering `source` is
   * permitted to declare the capability's `origin.kind`; mismatches are
   * dropped with a warning so untrusted callers cannot impersonate other
   * registration sources.
   *
   * Allowed combinations:
   * - source `'agent'`: origins `framework | agent | flow | skill | mcp | connector | mount`
   *   — never `client`, and never `app` (an in-process registrant must not be
   *   able to impersonate an embedded app)
   * - source `'client'`: origins `client` or `app`. The platform gateway is the
   *   only client peer; it forwards an embedded app's capabilities after binding
   *   each to the connection it arrived on. The runner cannot verify an `appId`.
   * - source `{ kind: 'app', appId }`: origin `app` only, and the origin's
   *   `appId` must match the source `appId`. For a direct app→runner connection;
   *   unused today.
   *
   * @param cap - capability to register (typically built via {@link defineCapability})
   * @param source - which side is doing the registration
   */
  register(cap: DefinedCapability, source: RegisterSource): void {
    if (!this.isOriginAllowed(source, cap.origin)) {
      this.logger.warn("capability registration rejected (origin mismatch)", {
        name: cap.name,
        source: describeSource(source),
        originKind: cap.origin.kind,
      });
      return;
    }
    this.byName.set(cap.name, cap);
    this.toolsChanged();
    this.logger.info("capability registered", {
      name: cap.name,
      side: cap.side,
      originKind: cap.origin.kind,
    });
  }

  /**
   * Remove a capability by name. No-op when the name is unknown.
   *
   * @param name - capability name
   */
  deregister(name: string): void {
    if (this.byName.delete(name)) {
      this.toolsChanged();
      this.logger.info("capability deregistered", { name });
    }
  }

  /**
   * List capabilities, optionally filtering by `side`, `origin` kind, or
   * `scope`. Returns the wire-format {@link Capability} shape (no internal
   * Zod schemas or handlers).
   */
  list(filter?: CapabilityListFilter): Capability[] {
    const all = Array.from(this.byName.values());
    const matching = filter
      ? all.filter(
          (c) =>
            (filter.side === undefined || c.side === filter.side) &&
            (filter.origin === undefined || c.origin.kind === filter.origin) &&
            (filter.scope === undefined || c.scope === filter.scope),
        )
      : all;
    return matching.map((c) => this.toWire(c));
  }

  /**
   * Resolve a capability by name. Returns the full {@link DefinedCapability}
   * (including the original Zod schemas + handler) for use by the bridge.
   *
   * @returns the capability or `null` when the name is not registered
   */
  resolve(name: string): DefinedCapability | null {
    return this.byName.get(name) ?? null;
  }

  /**
   * Build LLM tool descriptors for every registered capability whose
   * audience includes `'llm'` (the default when {@link Capability.audience}
   * is omitted). Bridge drivers consume this output directly when
   * assembling the tool list passed to the underlying LLM SDK.
   *
   * Capabilities scoped to `'runtime'` only (e.g. `host.refresh_credential`,
   * `runner.add_mount`) are filtered out so they never reach the model.
   */
  composeLLMTools(): LLMTool[] {
    return Array.from(this.byName.values())
      .filter((c) => hasAudience(c, "llm"))
      .map((c) => ({
        name: c.name,
        description: c.description,
        parameters: c.inputSchema,
      }));
  }

  /**
   * Compose a deterministic prompt section from every registered
   * capability whose audience includes `'llm'`. Ordering matches
   * `ORIGIN_PROMPT_ORDER` (framework first, then client, then agent-side
   * surfaces) so the agent sees a stable layout. Capabilities without a
   * `promptFragment` are skipped.
   */
  composePromptSection(): string {
    const groups = new Map<CapabilityOrigin["kind"], string[]>();
    for (const cap of this.byName.values()) {
      if (!cap.promptFragment) continue;
      if (!hasAudience(cap, "llm")) continue;
      const arr = groups.get(cap.origin.kind) ?? [];
      arr.push(cap.promptFragment);
      groups.set(cap.origin.kind, arr);
    }
    const lines: string[] = [];
    for (const kind of ORIGIN_PROMPT_ORDER) {
      const fragments = groups.get(kind);
      if (!fragments?.length) continue;
      for (const fragment of fragments) {
        lines.push(fragment);
      }
    }
    return lines.join("\n\n");
  }

  /**
   * Serialize the registry to wire-format capabilities for hibernation. The
   * runtime fields (`inputZod`, `outputZod`, `handler`) are dropped; only
   * the wire shape is kept.
   */
  serialize(): Capability[] {
    return Array.from(this.byName.values()).map((c) => this.toWire(c));
  }

  /**
   * Replay a wire snapshot taken via {@link serialize}. Wire-only capabilities
   * (no handler) are stored so {@link list} / {@link composeLLMTools} return
   * a complete picture; agent-side surfaces are expected to re-register
   * fully (with handlers) from their original sources on wake.
   *
   * Per the v2 design, only `client`-side capabilities are persisted across
   * hibernation by this method — agent-side ones come back via the
   * skill / flow / connector / mount adapters. Snapshot entries from other
   * origins are skipped on hydrate.
   *
   * Embedded-app capabilities carry an `app` origin, so the `client`-origin
   * check already excludes them; the reserved-prefix skip below is a backstop
   * against a capability that reaches the registry with a relabelled origin.
   */
  hydrate(snapshot: Capability[]): void {
    let hydrated = 0;
    for (const cap of snapshot) {
      if (cap.origin.kind !== "client") continue;
      // An app re-registers its capabilities when its connection reconnects. A
      // hydrated stub would outlive the connection and become a tool the agent
      // can see but never call.
      if (cap.name.startsWith(APP_CAPABILITY_PREFIX)) continue;
      const stub: DefinedCapability = {
        ...cap,
        inputZod: HYDRATED_PASSTHROUGH_ZOD,
        outputZod: undefined,
        handler: HYDRATED_REJECT_HANDLER,
      };
      this.byName.set(cap.name, stub);
      this.toolsChanged();
      hydrated++;
    }
    // `count` is what was actually restored; most of a snapshot is normally
    // skipped, so reporting `snapshot.length` alone would read as a no-op bug.
    this.logger.info("capability registry hydrated", { count: hydrated, total: snapshot.length });
  }

  /**
   * Invoke a registered capability. Validates the input against the Zod
   * schema, builds the per-handler logger, and runs the handler. Logs
   * `info "invoking"` and `info "ok"` (or `error "failed"`) under the
   * `capability:<origin>:<name>` slice for every call.
   *
   * @throws when the capability is unknown or the input fails validation
   */
  async invoke(
    name: string,
    input: unknown,
    baseCtx: Omit<HandlerContext, "log">,
  ): Promise<unknown> {
    baseCtx.signal?.throwIfAborted();
    const cap = this.resolve(name);
    if (!cap) {
      throw new Error(`Unknown capability: ${name}`);
    }
    const log = createLogger({
      kind: "capability",
      subkind: cap.origin.kind,
      instance: capabilityLogInstance(cap),
    });
    log.info("invoking", { input });
    try {
      const validated = cap.inputZod.parse(input);
      const result = await cap.handler(validated, { ...baseCtx, log });
      log.info("ok", { output: result });
      return result;
    } catch (err) {
      log.error("failed", err);
      throw err;
    }
  }

  /** True when `source` is allowed to register the given `origin`. */
  private isOriginAllowed(source: RegisterSource, origin: CapabilityOrigin): boolean {
    if (source === "client") {
      // The platform gateway is the only `client` source, and it forwards both
      // its own capabilities and those of embedded apps — having already bound
      // each app registration to the socket it arrived on. The runner cannot
      // verify an `appId`; the gateway is the enforcing layer for that.
      return origin.kind === "client" || origin.kind === "app";
    }
    if (source === "agent") {
      // The guard that matters: a skill, connector, or flow must not be able to
      // impersonate an app and register `app.*` tools.
      return origin.kind !== "client" && origin.kind !== "app";
    }
    // A direct app→runner connection. Unused today (apps reach the runner
    // through the gateway), kept so the source stays meaningful if one appears.
    return origin.kind === "app" && origin.appId === source.appId;
  }

  /** Strip the runtime-only fields from a {@link DefinedCapability}. */
  private toWire(cap: DefinedCapability): Capability {
    return {
      name: cap.name,
      displayName: cap.displayName,
      description: cap.description,
      inputSchema: cap.inputSchema,
      outputSchema: cap.outputSchema,
      side: cap.side,
      origin: cap.origin,
      scope: cap.scope,
      fireAndForget: cap.fireAndForget,
      requiresApproval: cap.requiresApproval,
      kind: cap.kind,
      userInvokable: cap.userInvokable,
      audience: cap.audience,
      promptFragment: cap.promptFragment,
      callTimeoutMs: cap.callTimeoutMs,
      render: cap.render,
    };
  }
}

/**
 * Compute a deterministic signature over a registry's wire-format
 * capabilities. Used by the session-resume cascade (tier-1 native SDK
 * resume) to detect toolset drift between hibernate and wake.
 *
 * The signature hashes a canonicalized projection of each capability:
 * `name + origin.kind + sha256(inputSchema)`. Capabilities are sorted
 * by `name` so registration order does not affect the result.
 *
 * Excluded from the hash:
 * - `displayName` and `userInvokable` — UI metadata only; do not affect
 *   what the LLM can call.
 * - `description` and `outputSchema` — text/shape changes that don't
 *   alter the call surface.
 *
 * Spec: `_devlog/specs/2026-05-05-session-resume-restart-design.md`
 * § "Capability signature".
 *
 * @param caps - Wire-format capabilities from {@link CapabilityRegistry.list}.
 * @returns A hex SHA-256 string that uniquely identifies the toolset.
 * @category Resume
 * @since 3.2.0
 * @docLink packages/runner/capabilities#compute-capability-signature
 */
export function computeCapabilitySignature(caps: readonly Capability[]): string {
  const canonical = caps
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({
      name: c.name,
      origin: c.origin.kind,
      schemaSha256: sha256(JSON.stringify(c.inputSchema ?? {})),
    }));
  return sha256(JSON.stringify(canonical));
}

function sha256(input: string): string {
  return createHash("sha256").update(input).digest("hex");
}

// Sentinel Zod schema used by `hydrate()` to keep the wire-format snapshot
// usable for `list`/`composeLLMTools` without forcing real handler reattach
// at hibernate-restore time. Actual invocation must go through a re-registered
// capability that comes back via `ConfigureCommand.capabilities` on wake.
const HYDRATED_PASSTHROUGH_ZOD: import("zod").ZodTypeAny = {
  parse: (v: unknown) => v,
  // The full Zod surface is not required here — `parse` is the only method
  // used by `invoke`, and hydrated stubs are not expected to be invoked
  // before the platform resends the live capability via configure.
} as unknown as import("zod").ZodTypeAny;

const HYDRATED_REJECT_HANDLER = async (): Promise<never> => {
  throw new Error(
    "Hydrated capability stub cannot be invoked — re-register via ConfigureCommand on wake.",
  );
};
